import { column, Css, emptyCell, GridColumn, GridDataRow, GridTable, simpleHeader } from "@homebound/beam";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { dateCell, emptyCellDash, tagCell } from "src/components";
import {
  BudgetSuperDrawer_CommitmentChangeOrderFragment,
  BudgetSuperDrawer_CommitmentFragment,
  BudgetSuperDrawer_CommitmentLineItemFragment,
  BudgetSuperDrawer_ProjectItemFragment,
} from "src/routes/projects/budget/components/BudgetSuperDrawer.generated";
import { commitmentStatusToTagTypeMapper, formatCentsToPrice, sum } from "src/utils";

type BudgetCommitmentsTableProps = { projectItems: BudgetSuperDrawer_ProjectItemFragment[] };

export function BudgetCommitmentsTable({ projectItems }: BudgetCommitmentsTableProps) {
  const commitmentLineItems = useMemo(() => projectItems.flatMap((pi) => pi.commitmentLineItems), [projectItems]);
  const rows = createRows(commitmentLineItems);
  const columns = createColumns(commitmentLineItems);

  return (
    <div>
      <div css={Css.smSb.h3.mbPx(10).$} data-testid="title">
        Commitments
      </div>
      <GridTable id="commitments" columns={columns} rows={rows} />
    </div>
  );
}

function createRows(commitmentLineItems: BudgetSuperDrawer_CommitmentLineItemFragment[]): GridDataRow<Row>[] {
  const [commitments, commitmentChangeOrders] = commitmentLineItems
    .map((li) => li.owner)
    .unique()
    .partition((cLike) => cLike.__typename === "Commitment");

  return [
    simpleHeader,
    ...(commitments as BudgetSuperDrawer_CommitmentFragment[]).map((c) => ({
      kind: "commitment" as const,
      id: c.id,
      data: c,
    })),
    ...(commitmentChangeOrders as BudgetSuperDrawer_CommitmentChangeOrderFragment[]).map((cco) => ({
      kind: "changeOrder" as const,
      id: cco.id,
      data: cco,
    })),
    {
      kind: "totals" as const,
      id: "total",
      data: {
        commitmentLikes: sum(
          commitments.sum(({ committedInCents }) => committedInCents),
          commitmentChangeOrders.sum(({ committedInCents }) => committedInCents),
        ),
        perItems: commitmentLineItems.sum((cli) => cli.committedInCents),
      },
    },
  ];
}

function createColumns(commitmentLineItems: BudgetSuperDrawer_CommitmentLineItemFragment[]): GridColumn<Row>[] {
  const commitmentLineItemsByOwner = commitmentLineItems.groupBy((cli) => cli.owner.id);
  return [
    column<Row>({
      header: "Commitment",
      commitment: ({ shortName, blueprintUrl }) => <Link to={blueprintUrl.path}>{shortName}</Link>,
      changeOrder: ({ shortName, blueprintUrl }) => <Link to={blueprintUrl.path}>{shortName}</Link>,
      totals: "Total",
      w: "100px",
    }),
    column<Row>({
      header: "Status",
      commitment: ({ status }) => tagCell(commitmentStatusToTagTypeMapper[status], status),
      changeOrder: ({ status }) => tagCell(commitmentStatusToTagTypeMapper[status], status),
      totals: emptyCell,
      w: "80px",
    }),
    column<Row>({
      header: "Trade Partner",
      commitment: ({ tradePartner }) => tradePartner?.name,
      changeOrder: ({ tradePartner }) => tradePartner?.name,
      totals: emptyCell,
      w: "190px",
    }),
    column<Row>({
      header: "Executed",
      commitment: ({ executionDate }) => dateCell(executionDate),
      changeOrder: ({ executionDate }) => dateCell(executionDate),
      totals: emptyCell,
      w: "100px",
    }),
    column<Row>({
      header: "Committed",
      commitment: ({ committedInCents }) => formatCentsToPrice(committedInCents),
      changeOrder: ({ committedInCents }) => formatCentsToPrice(committedInCents),
      totals: (data) => formatCentsToPrice(data.commitmentLikes),
      w: "100px",
    }),
    column<Row>({
      header: "Per Item Cost",
      commitment: ({ id }) => formatCentsToPrice(commitmentLineItemsByOwner[id].sum((cli) => cli.committedInCents)),
      changeOrder: ({ id }) => formatCentsToPrice(commitmentLineItemsByOwner[id].sum((cli) => cli.committedInCents)),
      totals: (data) => formatCentsToPrice(data.perItems),
      w: "100px",
    }),
    column<Row>({
      header: "Per Item Trade Paid",
      commitment: ({ id }) => formatCentsToPrice(commitmentLineItemsByOwner[id].sum((cli) => cli.paidInCents || 0)),
      changeOrder: ({ id }) => formatCentsToPrice(commitmentLineItemsByOwner[id].sum((cli) => cli.paidInCents || 0)),
      totals: emptyCell,
      w: "120px",
    }),
    column<Row>({
      header: "Per Item Unbilled",
      commitment: ({ id }) => formatCentsToPrice(commitmentLineItemsByOwner[id].sum((cli) => cli.unbilledInCents)),
      changeOrder: ({ id }) => formatCentsToPrice(commitmentLineItemsByOwner[id].sum((cli) => cli.unbilledInCents)),
      totals: emptyCell,
      w: "100px",
    }),

    column<Row>({
      header: "Source Agreement",
      commitment: ({ developmentCommitment, bidContractRevision }) => ({
        content: developmentCommitment?.name ? (
          <div css={Css.xs.$}>
            <Link to={developmentCommitment.blueprintUrl.path} target="_blank">
              {developmentCommitment?.name}
            </Link>
          </div>
        ) : bidContractRevision ? (
          <div css={Css.xs.$}>
            <Link to={bidContractRevision.blueprintUrl.path} target="_blank">
              {bidContractRevision?.bidContract?.displayName}
            </Link>
          </div>
        ) : (
          emptyCellDash
        ),
      }),
      changeOrder: emptyCell,
      totals: emptyCell,
      w: "290px",
    }),
  ];
}
type Row = { kind: "header" } | CommitmentRow | CommitmentChangeOrderRow | TotalRow;
type CommitmentRow = { kind: "commitment"; id: string; data: BudgetSuperDrawer_CommitmentFragment };

type CommitmentChangeOrderRow = {
  kind: "changeOrder";
  id: string;
  data: BudgetSuperDrawer_CommitmentChangeOrderFragment;
};

type TotalRow = { kind: "totals"; data: { commitmentLikes: number; perItems: number } };
